import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import {
  CheckCircle,
  CircleNotch,
  Star,
  ArrowRight,
} from "@phosphor-icons/react";

/**
 * /billing/success — landing page after a successful Stripe checkout.
 *
 * BEFORE this page existed, Stripe's `success_url` pointed at
 * `/billing/success?session_id=...` which had NO matching SPA route, so
 * the catch-all sent users back to `/` (homepage) — they thought
 * the payment crashed and lost confidence in the upgrade flow.
 * (Real-world bug: Gourmet Crepes reported "Web crashes half way
 *  through payment info. Can't upgrade to pro." in Feb 2026.)
 *
 * This page:
 *  - Confirms the session id is present (or shows a friendly fallback)
 *  - Polls /api/billing/upgrade-status for up to ~15s waiting for the
 *    Stripe webhook to flip the user's is_pro flag
 *  - Pivots to a celebratory "You're on Pro!" state with a CTA back
 *    into the dashboard / profile editor
 *  - Falls back to "Payment received, activation in progress" if the
 *    webhook is slow (Stripe occasionally delays). Tells the user
 *    they'll get an email + asks them to refresh in a minute.
 */
const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 15000;

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") || "";
  const [state, setState] = useState("polling"); // polling | active | pending
  const startedAt = useRef(Date.now());

  useEffect(() => {
    let cancelled = false;
    let timer = null;

    async function poll() {
      if (cancelled) return;
      try {
        const { data } = await api.get("/billing/upgrade-status");
        if (cancelled) return;
        if (data?.is_pro) {
          setState("active");
          // Set a session-scoped welcome flag so the next page the
          // owner lands on (usually /dashboard/profile-editor) can
          // fire a "You're on Pro!" toast — closes the loop for the
          // first minute after payment even if they blink past this
          // success screen.
          try {
            sessionStorage.setItem("tt_just_upgraded_pro", "1");
          } catch {
            /* private-mode Safari — non-fatal */
          }
          return;
        }
      } catch {
        /* swallow — keep polling */
      }
      if (Date.now() - startedAt.current >= POLL_TIMEOUT_MS) {
        if (!cancelled) setState("pending");
        return;
      }
      timer = setTimeout(poll, POLL_INTERVAL_MS);
    }

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  return (
    <div
      className="min-h-[calc(100vh-4rem)] grid place-items-center px-4 py-10 bg-gradient-to-br from-primary/5 via-background to-background"
      data-testid="billing-success-page"
    >
      <div className="w-full max-w-md text-center">
        {state === "polling" && (
          <div data-testid="billing-success-polling">
            <CircleNotch className="h-10 w-10 mx-auto animate-spin text-primary" />
            <h1 className="mt-5 text-2xl sm:text-3xl font-bold tracking-tight">
              Activating your Pro plan…
            </h1>
            <p className="mt-3 text-sm text-muted-foreground">
              We received your payment. Hang tight — Stripe usually
              confirms within a few seconds.
            </p>
          </div>
        )}

        {state === "active" && (
          <div data-testid="billing-success-active">
            <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-emerald-500/15 ring-4 ring-emerald-500/25 mb-4">
              <CheckCircle weight="fill" className="h-9 w-9 text-emerald-500" />
            </div>
            <div className="inline-flex items-center gap-1.5 h-7 px-3 rounded-full bg-primary/10 text-primary text-[11px] font-bold uppercase tracking-wider mb-3">
              <Star weight="fill" className="h-3.5 w-3.5" /> TruckTrackr Pro
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
              You&apos;re on Pro!
            </h1>
            <p className="mt-3 text-sm text-muted-foreground">
              Your profile editor, calendar sync, and event marketplace
              filters are unlocked. A receipt is on its way to your inbox.
            </p>
            <div className="mt-7 space-y-2">
              <Link to="/dashboard/profile-editor">
                <Button
                  className="w-full"
                  data-testid="billing-success-edit-profile"
                >
                  Customize your public profile
                  <ArrowRight weight="bold" className="h-4 w-4 ml-1.5" />
                </Button>
              </Link>
              <Link to="/dashboard">
                <Button
                  variant="outline"
                  className="w-full"
                  data-testid="billing-success-dashboard"
                >
                  Go to dashboard
                </Button>
              </Link>
            </div>
          </div>
        )}

        {state === "pending" && (
          <div data-testid="billing-success-pending">
            <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-amber-500/15 ring-4 ring-amber-500/25 mb-4">
              <CheckCircle weight="fill" className="h-9 w-9 text-amber-500" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
              Payment received
            </h1>
            <p className="mt-3 text-sm text-muted-foreground">
              Stripe is still finalizing your subscription. This usually
              takes under a minute. You&apos;ll get an email the moment
              your Pro features unlock — feel free to refresh in a bit
              or head back to the dashboard.
            </p>
            <div className="mt-7 space-y-2">
              <Button
                className="w-full"
                onClick={() => window.location.reload()}
                data-testid="billing-success-refresh"
              >
                Refresh now
              </Button>
              <Link to="/dashboard">
                <Button
                  variant="outline"
                  className="w-full"
                  data-testid="billing-success-dashboard"
                >
                  Go to dashboard
                </Button>
              </Link>
            </div>
            {sessionId && (
              <p className="mt-4 text-[11px] text-muted-foreground/70 font-mono break-all">
                Reference: {sessionId}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
